import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { apiGet, apiPost, ApiError } from "../shared/api.js";
import { copy, errorText } from "../shared/copy.js";
import { Button } from "../components/Button.js";
import { PassportCover } from "../components/PassportCover.js";
import { PassportBook } from "../components/PassportBook.js";
import { PhotoUploader } from "../components/PhotoUploader.js";
import { ReviewPrompt } from "../components/ReviewPrompt.js";
import { ShareSheet } from "../components/ShareSheet.js";
import { BonusesSection } from "../components/BonusesSection.js";
import { DeleteMyData } from "../components/DeleteMyData.js";
import { TextField, Checkbox } from "../components/Field.js";
import { PhoneField } from "../components/PhoneField.js";
import { track } from "../shared/analytics.js";
import { isTelegram } from "../shared/tg.js";
import styles from "./CityRoute.module.css";

// Public DTO shapes redeclared inline — the client bundle doesn't pull
// Zod, so the API surface is duplicated as tiny TS interfaces.

interface CityDto {
  slug: string;
  code: string;
  name: string;
  timezone: string;
  reviewGis: string | null;
  reviewYandex: string | null;
}

interface KnownPassport {
  id: string;
  displayNumber: string;
  childName: string;
}

interface SessionStart {
  sessionId: string;
  city: { slug: string; name: string; code: string };
  reservedNumber: string;
  knownParent: boolean;
  // Empty for new devices / anonymous sessions. Populated only when a
  // valid sp_parent cookie identifies an existing parent (D-011 auth).
  parentPassports: KnownPassport[];
}

interface EnterCodeResp {
  gameId: string;
  game: {
    venueName: string;
    startsAt: string | null;
    reviewGis: string | null;
    reviewYandex: string | null;
  };
}

interface CompleteResp {
  passports: Array<{
    id: string;
    displayNumber: string;
    totem: string;
    childName: string;
    childBirthdate: string; // YYYY-MM-DD
    rank: string;
    questsCount: number;
    refCode: string;
    photoUrl: string | null;
  }>;
  reel: { ready: boolean; url: string | null };
  bonus: { type: string; expiresAt: string } | null;
}

type Screen =
  | { name: "loading" }
  | { name: "error"; code: string }
  | { name: "S1"; city: CityDto; session: SessionStart }
  | { name: "S2"; city: CityDto; session: SessionStart }
  | { name: "S3"; city: CityDto; session: SessionStart; game: EnterCodeResp["game"] }
  | {
      name: "S4";
      city: CityDto;
      session: SessionStart;
      result: CompleteResp;
      // Optional — carries the venue's review URLs when the parent
      // finished the fresh flow (S3 → S4). Cross-device recovery lands
      // here without a game, in which case ReviewPrompt falls back to
      // City-level URLs.
      game?: EnterCodeResp["game"];
    }
  // Standalone bonuses page — reachable from S4 via the "Забрать
  // подарки" CTA. Kept as a separate phase (not a modal) so the parent
  // can browse promos with the passport out of view; back-arrow drops
  // straight back into S4 without re-fetching anything.
  | {
      name: "bonuses";
      city: CityDto;
      session: SessionStart;
      result: CompleteResp;
      game?: EnterCodeResp["game"];
    };

export function CityRoute() {
  const { citySlug = "" } = useParams();
  const [screen, setScreen] = useState<Screen>({ name: "loading" });

  useEffect(() => {
    let cancelled = false;

    async function boot() {
      try {
        const city = await apiGet<CityDto>(
          `/api/cities/${encodeURIComponent(citySlug)}`,
        );
        const session = await apiPost<SessionStart>("/api/session/start", {
          citySlug,
          env: isTelegram() ? "TELEGRAM" : "BROWSER",
        });
        if (cancelled) return;
        // qr_scanned marks the actual funnel entry — the parent tapped a
        // coin/link and got as far as a live session. session_started is
        // the server-side twin; both fire once per session.
        track({
          name: "qr_scanned",
          sessionId: session.sessionId,
          props: { citySlug },
        });
        setScreen({ name: "S1", city, session });
      } catch (e) {
        if (cancelled) return;
        const code = e instanceof ApiError ? e.code : "INTERNAL_ERROR";
        setScreen({ name: "error", code });
      }
    }
    void boot();
    return () => {
      cancelled = true;
    };
  }, [citySlug]);

  if (screen.name === "loading") return <Loading />;
  if (screen.name === "error") return <ErrorPane code={screen.code} />;

  return (
    <div className={styles.page}>
      {screen.name === "S1" && (
        <>
          <MagicBanner />
          <PassportCover
            displayNumber={screen.session.reservedNumber}
            cityLabel={screen.city.name}
          />
          <h1 className={styles.headline}>{copy.s1.title}</h1>
          <p className={styles.sub}>{copy.s1.subtitle}</p>
          <div className={styles.cta}>
            <Button
              block
              onClick={() => {
                track({
                  name: "activation_started",
                  sessionId: screen.session.sessionId,
                });
                setScreen({ ...screen, name: "S2" });
              }}
            >
              {copy.s1.cta}
            </Button>
          </div>
        </>
      )}
      {screen.name === "S2" && (
        <S2Code
          session={screen.session}
          onConfirmed={(game) =>
            setScreen({ ...screen, name: "S3", game })
          }
        />
      )}
      {screen.name === "S3" && (
        <S3
          city={screen.city}
          session={screen.session}
          game={screen.game}
          onIssued={(result) =>
            setScreen({ ...screen, name: "S4", result, game: screen.game })
          }
        />
      )}
      {screen.name === "S4" && (
        <S4
          city={screen.city}
          session={screen.session}
          result={screen.result}
          {...(screen.game ? { game: screen.game } : {})}
          onOpenBonuses={() =>
            setScreen({ ...screen, name: "bonuses" })
          }
        />
      )}
      {screen.name === "bonuses" && (
        <BonusesPage
          result={screen.result}
          onBack={() => setScreen({ ...screen, name: "S4" })}
        />
      )}
    </div>
  );
}

// ─────────────────────────── S2 — 4-char activation code

function S2Code(props: {
  session: SessionStart;
  onConfirmed: (game: EnterCodeResp["game"]) => void;
}) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const canSubmit = /^[A-Z0-9]{4}$/.test(code) && !busy;

  async function submit() {
    setBusy(true);
    setError("");
    try {
      const result = await apiPost<EnterCodeResp>(
        "/api/activation/enter-code",
        { sessionId: props.session.sessionId, code },
      );
      props.onConfirmed(result.game);
    } catch (e) {
      setError(e instanceof ApiError ? errorText(e.code) : copy.common.somethingWrong);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={styles.card}>
      <h1 className={styles.headline}>{copy.s2.title}</h1>
      <p className={styles.sub}>{copy.s2.subtitle}</p>
      <TextField
        label={copy.s2.title}
        placeholder={copy.s2.codePlaceholder}
        value={code}
        // Force uppercase + strip anything outside A-Z0-9 so the input can
        // be scanned or typed however the parent likes.
        onChange={(e) => {
          const cleaned = e.target.value
            .toUpperCase()
            .replace(/[^A-Z0-9]/g, "")
            .slice(0, 4);
          setCode(cleaned);
        }}
        // autocomplete="off" alone is ignored by Chrome/Yandex-Browser —
        // add a unique field name, one-time-code hint, and a scary
        // data-form-type so no password manager tries to hijack it.
        name="quest-code"
        inputMode="text"
        autoComplete="one-time-code"
        autoCorrect="off"
        spellCheck={false}
        autoCapitalize="characters"
        maxLength={4}
        data-form-type="other"
        data-lpignore="true"
        data-1p-ignore="true"
      />
      {error && <p className={styles.error}>{error}</p>}
      <Button block disabled={!canSubmit} onClick={submit}>
        {copy.s2.cta}
      </Button>
    </div>
  );
}

// ─────────────────────────── S3 — child form (with returning-parent branch)

function S3(props: {
  city: CityDto;
  session: SessionStart;
  game: EnterCodeResp["game"];
  onIssued: (result: CompleteResp) => void;
}) {
  const [name, setName] = useState("");
  const [birthdate, setBirthdate] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [mediaConsent, setMediaConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // Device recognition comes from /session/start (sp_parent cookie) —
  // no phone-based lookup on the client. Anonymous devices always see
  // the full form.
  const knownPassports = props.session.parentPassports;
  const [forceNewChild, setForceNewChild] = useState(false);
  const showKnownParent =
    props.session.knownParent && knownPassports.length > 0 && !forceNewChild;
  const phoneComplete = /^\+\d{7,15}$/.test(phone);

  const canSubmit =
    !busy &&
    consent &&
    !showKnownParent &&
    phoneComplete &&
    name.trim().length >= 2 &&
    birthdate.length === 10;

  async function submitNewChild() {
    setBusy(true);
    setError("");
    try {
      const body: Record<string, unknown> = {
        kind: "new_child",
        sessionId: props.session.sessionId,
        children: [{ name: name.trim(), birthdate }],
        phone,
        consent: true,
        mediaConsent,
        isHostParent: false,
      };
      if (email) body.email = email;
      const result = await apiPost<CompleteResp>(
        "/api/activation/complete",
        body,
      );
      props.onIssued(result);
    } catch (e) {
      setError(e instanceof ApiError ? errorText(e.code) : copy.common.somethingWrong);
    } finally {
      setBusy(false);
    }
  }

  async function submitSameChild(passportId: string) {
    setBusy(true);
    setError("");
    try {
      const result = await apiPost<CompleteResp>(
        "/api/activation/complete",
        {
          kind: "same_child",
          sessionId: props.session.sessionId,
          passportId,
        },
      );
      props.onIssued(result);
    } catch (e) {
      setError(e instanceof ApiError ? errorText(e.code) : copy.common.somethingWrong);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <h1 className={styles.headline}>{copy.s3.title}</h1>
      <div className={styles.card}>
        {showKnownParent ? (
          <>
            <p className={styles.sub}>{copy.s3.knownParent.hint}</p>
            {knownPassports.map((p) => (
              <Button
                key={p.id}
                block
                disabled={busy}
                onClick={() => submitSameChild(p.id)}
              >
                {copy.s3.knownParent.sameChild(p.childName)}
              </Button>
            ))}
            <Button
              variant="ghost"
              disabled={busy}
              onClick={() => setForceNewChild(true)}
            >
              {copy.s3.knownParent.otherChild}
            </Button>
            {error && <p className={styles.error}>{error}</p>}
          </>
        ) : (
          <>
            <PhoneField value={phone} onChange={setPhone} />
            <TextField
              label={copy.s3.childName}
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={40}
            />
            <TextField
              label={copy.s3.childBirthdate}
              type="date"
              value={birthdate}
              onChange={(e) => setBirthdate(e.target.value)}
              hint={copy.s3.childBirthdateHint}
            />
            <TextField
              label={copy.s3.email}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              hint={copy.s3.emailHint}
              autoComplete="email"
            />
            <Checkbox
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
            >
              {copy.s3.consentPd}
            </Checkbox>
            <Checkbox
              checked={mediaConsent}
              onChange={(e) => setMediaConsent(e.target.checked)}
            >
              {copy.s3.consentMedia}
            </Checkbox>
            {error && <p className={styles.error}>{error}</p>}
            <Button
              block
              disabled={!canSubmit}
              onClick={submitNewChild}
            >
              {copy.s3.cta}
            </Button>
            <RestoreLink citySlug={props.city.slug} />
          </>
        )}
      </div>
    </>
  );
}

// ─────────────────────────── S4 — issued passport

function S4(props: {
  city: CityDto;
  session: SessionStart;
  result: CompleteResp;
  game?: EnterCodeResp["game"];
  onOpenBonuses: () => void;
}) {
  const p0 = props.result.passports[0]!;
  const today = new Date().toISOString().slice(0, 10);
  // Local override so the passport picture updates right after upload
  // without a full page reload.
  const [photoUrl, setPhotoUrl] = useState<string | null>(p0.photoUrl);
  return (
    <>
      <PassportBook
        displayNumber={p0.displayNumber}
        cityName={props.city.name}
        totem={p0.totem}
        childName={p0.childName}
        issueDate={today}
        rank={p0.rank}
        questsCount={p0.questsCount}
        photoUrl={photoUrl}
      />
      <h1 className={styles.headline}>{copy.s4.titleWithName(p0.childName)}</h1>
      <PhotoUploader
        passportId={p0.id}
        refCode={p0.refCode}
        currentPhotoUrl={photoUrl}
        onUploaded={setPhotoUrl}
      />
      <div className={styles.cta}>
        {props.result.reel.ready && props.result.reel.url ? (
          <Button
            block
            onClick={() => {
              track({
                name: "reel_opened",
                sessionId: props.session.sessionId,
                passportId: p0.id,
              });
              window.open(props.result.reel.url!, "_blank");
            }}
          >
            {copy.s4.watchReel}
          </Button>
        ) : (
          <p className={styles.sub}>{copy.s4.reelPending}</p>
        )}
        <Button block variant="secondary" onClick={props.onOpenBonuses}>
          {copy.s4.openBonuses}
        </Button>
      </div>
      <ShareSheet
        passportId={p0.id}
        refCode={p0.refCode}
        childName={p0.childName}
      />
      <ReviewPrompt
        sessionId={props.session.sessionId}
        cityGisUrl={props.game?.reviewGis ?? props.city.reviewGis}
        cityYandexUrl={props.game?.reviewYandex ?? props.city.reviewYandex}
      />
      <DeleteMyData passportId={p0.id} refCode={p0.refCode} />
    </>
  );
}

// ─────────────────────────── Bonuses page — standalone

function BonusesPage(props: {
  result: CompleteResp;
  onBack: () => void;
}) {
  const p0 = props.result.passports[0]!;
  return (
    <>
      <Button variant="ghost" onClick={props.onBack}>
        ← {copy.s4.backToPassport}
      </Button>
      <BonusesSection passportId={p0.id} refCode={p0.refCode} />
    </>
  );
}

// ─────────────────────────── auxiliaries

// D-004 layer 3: inline expander at the bottom of the S3 form. Parent
// enters their email, backend emails a magic-link that sets the
// sp_parent cookie via /api/auth/magic-link/consume?redirect=/{city}.
function RestoreLink(props: { citySlug: string }) {
  const [expanded, setExpanded] = useState(false);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  async function submit() {
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      setError("Проверьте адрес почты");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await apiPost("/api/auth/magic-link/request", { email });
      setSent(true);
    } catch (e) {
      setError(e instanceof ApiError ? errorText(e.code) : copy.common.somethingWrong);
    } finally {
      setBusy(false);
    }
  }

  if (!expanded) {
    return (
      <Button variant="ghost" onClick={() => setExpanded(true)}>
        {copy.s3.restore.link}
      </Button>
    );
  }

  if (sent) {
    return <p className={styles.sub}>{copy.s3.restore.sent(email)}</p>;
  }

  return (
    <>
      <p className={styles.sub}>{copy.s3.restore.hint}</p>
      <TextField
        label={copy.s3.restore.emailLabel}
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        autoComplete="email"
      />
      {error && <p className={styles.error}>{error}</p>}
      <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "var(--s-3)" }}>
        <Button variant="secondary" onClick={() => setExpanded(false)} disabled={busy}>
          {copy.s3.restore.cancel}
        </Button>
        <Button block onClick={submit} disabled={busy}>
          {copy.s3.restore.submit}
        </Button>
      </div>
    </>
  );
}

// One-off banner on the S1/S2 area when the URL carries ?magic=<reason>
// after a failed magic-link consumption. Auto-dismisses via useEffect
// on the first render — we drop the query param but keep the reason
// in state so the banner is visible until the parent taps the close.
function MagicBanner() {
  const [reason, setReason] = useState<string | null>(null);
  useEffect(() => {
    const url = new URL(window.location.href);
    const r = url.searchParams.get("magic");
    if (!r) return;
    setReason(r);
    url.searchParams.delete("magic");
    window.history.replaceState({}, "", url.pathname + url.search);
  }, []);
  if (!reason) return null;
  const message =
    copy.s3.magicBanner[reason as keyof typeof copy.s3.magicBanner] ??
    copy.s3.magicBanner.invalid;
  return (
    <div className={styles.card} style={{ background: "var(--c-clay)", color: "white" }}>
      <p style={{ margin: 0 }}>{message}</p>
      <Button variant="secondary" onClick={() => setReason(null)}>
        Понятно
      </Button>
    </div>
  );
}

function Loading() {
  return (
    <div className={styles.page}>
      <p className={styles.sub}>{copy.common.loading}</p>
    </div>
  );
}

function ErrorPane({ code }: { code: string }) {
  return (
    <div className={styles.page}>
      <h1 className={styles.headline}>{copy.common.somethingWrong}</h1>
      <p className={styles.sub}>{errorText(code)}</p>
      <Button onClick={() => window.location.reload()}>
        {copy.common.retry}
      </Button>
    </div>
  );
}
